import dayjs from "dayjs";
import type {FormikValues} from "formik";
import {
    EventOptionCreateDtoInput,
    EventOptionUpdateDtoInput,
    OptionConditionType,
    OptionPriceEffect,
    OptionQuestionType
} from "../../GraphQL/generated/graphql";

type EventOptionNumberValidationLabels = {
    price: string;
    choiceLabelHr: string;
    choiceLabelEn: string;
    requiredMessage: (field: string) => string;
    numberMessage: (field: string) => string;
};

const defaultNumberValidationLabels: EventOptionNumberValidationLabels = {
    price: "Price",
    choiceLabelHr: "Croatian label",
    choiceLabelEn: "English label",
    requiredMessage: field => `${field} is required.`,
    numberMessage: field => `${field} must be a number.`
};

const textValue = (value: unknown) => value == null ? "" : String(value);

const normalizeNumberValue = (value: unknown) => {
    if (typeof value === "string") {
        return value.trim().replace(",", ".");
    }

    return value;
};

const throwFieldError = (fieldName: "price", message: string): never => {
    throw {errors: {[fieldName]: [message]}};
};

const parseRequiredNumber = (
    value: unknown,
    fieldName: "price",
    labels: EventOptionNumberValidationLabels
) => {
    const normalized = normalizeNumberValue(value);
    if (normalized == null || normalized === "") {
        throwFieldError(fieldName, labels.requiredMessage(labels[fieldName]));
    }

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
        throwFieldError(fieldName, labels.numberMessage(labels[fieldName]));
    }

    return parsed;
};

const parseOptionalId = (value: unknown, fallback: number) => {
    const parsed = Number(normalizeNumberValue(value ?? fallback));
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseOptionalPositiveId = (value: unknown): number | null => {
    const parsed = Number(normalizeNumberValue(value));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const parseBoolean = (value: unknown) =>
    value === true || value === "true";

const optionalDateValue = (value: unknown) => {
    const text = textValue(value).trim();
    if (!text) {
        return null;
    }

    const parsed = dayjs(text);
    return parsed.isValid() ? `${parsed.format("YYYY-MM-DD")}T12:00:00.000Z` : null;
};

const supportedQuestionTypes = new Set<unknown>([
    OptionQuestionType.YesNo,
    OptionQuestionType.SingleChoice,
    OptionQuestionType.MultiChoice,
    OptionQuestionType.Text
]);

type SupportedOptionQuestionType = OptionQuestionType;

type EventOptionChoiceInput = {
    id: number;
    value: string;
    labelHr: string;
    labelEn: string;
    order: number;
    isActive: boolean;
};

type EventOptionAllowedFeeTypeFormRow = {
    feeTypeId?: number | string | null;
};

type EventOptionFormDataWithChoices = Omit<EventOptionCreateDtoInput, "questionType" | "priceEffect" | "allowedFeeTypeIds" | "visibilityDependsOnEventOptionId" | "visibilityDependsOnExpectedAnswer"> & {
    id?: number;
    choices?: EventOptionChoiceInput[];
    allowedFeeTypes?: EventOptionAllowedFeeTypeFormRow[];
    visibilityDependsOnEventOptionId?: number | string | null;
    visibilityDependsOnExpectedAnswer?: boolean | string | null;
    isCharged?: boolean;
    tracksUsageOrAttendance?: boolean | string | null;
    publicRegistrationEndDate?: string | null;
    questionType: SupportedOptionQuestionType;
};

type EventOptionCreateDtoInputWithChoices = Omit<EventOptionCreateDtoInput, "questionType"> & {
    choices?: EventOptionChoiceInput[];
    questionType: SupportedOptionQuestionType;
};

type EventOptionUpdateDtoInputWithChoices = Omit<EventOptionUpdateDtoInput, "questionType"> & {
    choices?: EventOptionChoiceInput[];
    questionType: SupportedOptionQuestionType;
};

export const normalizeSupportedEventOptionQuestionType = (value: unknown): SupportedOptionQuestionType =>
    supportedQuestionTypes.has(value) ? value as SupportedOptionQuestionType : OptionQuestionType.YesNo;

export const isEventOptionCharged = (priceEffect: unknown): boolean =>
    priceEffect == null || priceEffect === OptionPriceEffect.AddLineItem;

const slugifyChoiceValue = (label: string, fallbackOrder: number) => {
    const normalized = label
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return normalized || `choice-${fallbackOrder}`;
};

export const formatEventOptionChoiceRows = (choices: EventOptionChoiceInput[] | undefined | null): EventOptionChoiceInput[] =>
    [...(choices ?? [])]
        .filter(choice => choice?.isActive !== false)
        .sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0))
        .map((choice, index) => ({
            id: Number(choice.id ?? 0),
            value: textValue(choice.value),
            labelHr: textValue(choice.labelHr),
            labelEn: textValue(choice.labelEn),
            order: index + 1,
            isActive: choice.isActive !== false
        }));

export const createDefaultEventOptionFormData = (eventId: number): EventOptionFormDataWithChoices => ({
    eventId,
    labelHr: "",
    labelEn: "",
    descriptionHr: "",
    descriptionEn: "",
    vatRate: 0,
    conditionType: OptionConditionType.Always,
    categoryCodes: "",
    dependsOnCode: "",
    dependsOnValue: "",
    questionType: OptionQuestionType.YesNo,
    isCharged: true,
    tracksUsageOrAttendance: false,
    allowedFeeTypes: [],
    visibilityDependsOnEventOptionId: null,
    visibilityDependsOnExpectedAnswer: null,
    price: 0,
    publicRegistrationEndDate: "",
    isActive: true,
    choices: []
});

const throwChoiceErrors = (errors: Record<string, string[]>): never => {
    throw {errors};
};

const normalizeChoiceRows = (
    value: unknown,
    labels: EventOptionNumberValidationLabels
): EventOptionChoiceInput[] => {
    const rows = Array.isArray(value) ? value : [];
    const errors: Record<string, string[]> = {};

    const normalizedRows = rows.map((row: any, index) => {
        const labelHr = textValue(row?.labelHr).trim();
        const labelEn = textValue(row?.labelEn).trim();

        if (!labelHr) {
            errors[`choices[${index}].labelHr`] = [labels.requiredMessage(labels.choiceLabelHr)];
        }

        if (!labelEn) {
            errors[`choices[${index}].labelEn`] = [labels.requiredMessage(labels.choiceLabelEn)];
        }

        const order = index + 1;
        const fallbackLabel = labelEn || labelHr;

        return {
            id: Number(row?.id ?? 0) || 0,
            value: textValue(row?.value).trim() || slugifyChoiceValue(fallbackLabel, order),
            labelHr,
            labelEn,
            order,
            isActive: row?.isActive == null ? true : parseBoolean(row.isActive)
        };
    });

    if (Object.keys(errors).length > 0) {
        throwChoiceErrors(errors);
    }

    return normalizedRows;
};

const normalizeAllowedFeeTypeRows = (value: unknown): number[] => {
    const rows = Array.isArray(value) ? value : [];
    return Array.from(new Set(rows
        .map((row: any) => Number(normalizeNumberValue(row?.feeTypeId)))
        .filter((feeTypeId: number) => Number.isInteger(feeTypeId) && feeTypeId > 0)));
};

const normalizeVisibilityDependency = (values: FormikValues) => {
    const dependencyOptionId = parseOptionalPositiveId(values.visibilityDependsOnEventOptionId);
    if (!dependencyOptionId) {
        return {
            visibilityDependsOnEventOptionId: null,
            visibilityDependsOnExpectedAnswer: null
        };
    }

    return {
        visibilityDependsOnEventOptionId: dependencyOptionId,
        // The "expected answer" select shows "Yes" when it has no value, so only an explicit "No" saves false.
        visibilityDependsOnExpectedAnswer: !(values.visibilityDependsOnExpectedAnswer === false
            || values.visibilityDependsOnExpectedAnswer === "false")
    };
};

const parseActivityPrice = (
    value: unknown,
    isCharged: boolean,
    labels: EventOptionNumberValidationLabels
) => {
    if (!isCharged) {
        return 0;
    }

    return parseRequiredNumber(value, "price", labels);
};

const buildEventOptionBasePayload = (
    values: FormikValues,
    fallbackEventId: number,
    labels: EventOptionNumberValidationLabels = defaultNumberValidationLabels
) => {
    const isCharged = values.isCharged == null
        ? isEventOptionCharged(values.priceEffect)
        : parseBoolean(values.isCharged);
    const questionType = isCharged
        ? OptionQuestionType.YesNo
        : normalizeSupportedEventOptionQuestionType(values.questionType);
    const payload = {
        eventId: parseOptionalId(values.eventId, fallbackEventId),
        labelHr: textValue(values.labelHr),
        labelEn: textValue(values.labelEn),
        descriptionHr: textValue(values.descriptionHr),
        descriptionEn: textValue(values.descriptionEn),
        vatRate: 0,
        conditionType: (values.conditionType ?? OptionConditionType.Always) as OptionConditionType,
        categoryCodes: textValue(values.categoryCodes),
        dependsOnCode: textValue(values.dependsOnCode),
        dependsOnValue: textValue(values.dependsOnValue),
        questionType,
        priceEffect: isCharged ? OptionPriceEffect.AddLineItem : OptionPriceEffect.NoCharge,
        price: parseActivityPrice(values.price, isCharged, labels),
        publicRegistrationEndDate: optionalDateValue(values.publicRegistrationEndDate),
        tracksUsageOrAttendance: parseBoolean(values.tracksUsageOrAttendance),
        allowedFeeTypeIds: normalizeAllowedFeeTypeRows(values.allowedFeeTypes),
        ...normalizeVisibilityDependency(values),
        isActive: values.isActive == null ? true : parseBoolean(values.isActive)
    };

    if (questionType === OptionQuestionType.SingleChoice || questionType === OptionQuestionType.MultiChoice) {
        return {
            ...payload,
            choices: normalizeChoiceRows(values.choices, labels)
        };
    }

    return {
        ...payload,
        choices: []
    };
};

export const buildEventOptionCreateDto = (
    values: FormikValues,
    fallbackEventId: number,
    labels?: EventOptionNumberValidationLabels
): EventOptionCreateDtoInputWithChoices => ({
    ...buildEventOptionBasePayload(values, fallbackEventId, labels)
});

export const buildEventOptionUpdateDto = (
    values: FormikValues,
    fallbackEventId: number,
    labels?: EventOptionNumberValidationLabels
): EventOptionUpdateDtoInputWithChoices => ({
    id: parseOptionalId(values.id, 0),
    ...buildEventOptionBasePayload(values, fallbackEventId, labels)
});
